import {
  CountPCsTimeSlotsBeforeDate,
  getPCsTimeSlots,
  getPCsTimeSlotsBeforeDate,
  getPCsTimeSlotsByPeriod,
  getPCsTimeSlotsByPeriodRelativeToStart,
  getTimeslotsByCondition,
} from "@/api/gql/timeslots";
import { LogLevel, LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import {
  IKeyValue,
  ITimeSlot,
  ISyncUpdates,
  ISyncField,
} from "@/types/db/timeslots";
import { GetPCsTimeslotsQuery } from "@/types/gql/generated/graphql";
import dayjs from "dayjs";
import { nowDB } from "./dates";
import { getProductCategoriesByStockIds } from "@/dexie/product-category";
import {
  updateTimeslotsAvailability,
  queryTimeslotsByTicketGroup,
  bulkUpsertTimeslots,
  queryTimeslotsByProductCategories,
} from "@/dexie/timeslots";
import { getNonRecurringSeatingPCs } from "@/dexie/product-category";
import { pruneStaleRecords } from "@/dexie/helpers";
import { dexieDB } from "@/services/dexie-database";

/**
 * Fetches all the timeslots of a given list of product categories
 * and stores them in Dexie database.
 * @param ids Product Category ids
 */
export async function fetchAllPCsTimeslots(
  ids: number[],
  signal?: AbortSignal
) {
  // check if the app is online before start fetching
  if (!window.navigator.onLine) {
    return;
  }

  const now = Date.now();
  const monthMilliseconds = 1000 * 60 * 60 * 24 * 1; // The extra day will be cut to go back to the midnight
  const monthInFuture = now + monthMilliseconds;
  // Now periodEnd can correspond to a moment during the day, we need to make it corresponds to the midnight
  // so that we avoid pulling half of the timeslots of the last day of the period
  const periodEndMidnight = new Date(monthInFuture).setHours(0, 0, 0, 0);

  // Lets first get the count of timeslots
  const count = await CountPCsTimeSlotsBeforeDate(ids, periodEndMidnight);
  const timeslotsCount = count.timeslots ? count.timeslots : 1;

  const limit = 500;
  let skip = 0;
  const freshTimeslotIds = new Set<number>();

  // eslint-disable-next-line
  while (true) {
    if (signal?.aborted) break;
    const now = Date.now();
    const monthMilliseconds = 1000 * 60 * 60 * 24 * 1; // The extra day will be cut to go back to the midnight
    const monthInFuture = now + monthMilliseconds;
    // Now periodEnd can correspond to a moment during the day, we need to make it corresponds to the midnight
    // so that we avoid pulling half of the timeslots of the last day of the period
    const periodEndMidnight = new Date(monthInFuture).setHours(0, 0, 0, 0);

    const slots = (
      await getPCsTimeSlotsBeforeDate(ids, periodEndMidnight, {
        skip,
        limit,
      })
    ).timeslots;
    // break loop when no more slots are available
    if (!slots || !slots.length) {
      break;
    }

    const localTimeslots = toLocalTimeslots(slots);
    await bulkUpsertTimeslots(localTimeslots);
    for (const ts of localTimeslots) {
      freshTimeslotIds.add(ts.id);
    }

    skip += slots.length;

    // Why 40? because we decided that timeslots progress is going to be 40% from the bar
    const progress = Math.round((localTimeslots.length * 40) / timeslotsCount);
    POSConfigModule.setTimeslotsLoaded(
      POSConfigModule.timeslotsLoaded + progress
    );
  }

  // Prune any stale timeslots from the previous config that are no longer present
  if (!signal?.aborted) {
    await pruneStaleRecords(dexieDB.timeslots as any, freshTimeslotIds);
  }
}

/**
 *
 * @param slots fetched from Graphql server
 * @returns
 */
export function toLocalTimeslots(
  slots: GetPCsTimeslotsQuery["timeslots"],
  isParentPC?: boolean
): ITimeSlot[] {
  const timeslots: ITimeSlot[] = [];

  if (!slots) {
    return timeslots;
  }

  for (const slot of slots) {
    if (!slot) {
      continue;
    }

    const {
      id,
      categoryId,
      startDateTime,
      startDateTimeTz,
      endDateTime,
      endDateTimeTz,
      availability,
      availabilityUpdated,
      booked,
      bookable,
      estimatedTotalCapacity,
      timeZone,
      computedPrices,
      psc,
      eventKey,
    } = slot;

    const timeslot: ITimeSlot = {
      _id: String(id),
      id: id as number,
      categoryId: categoryId as number,
      startDateTime: dayjs.utc(startDateTime as string).toISOString(),
      startDateTimeTz: startDateTimeTz as string,
      endDateTime: dayjs.utc(endDateTime as string).toISOString(),
      endDateTimeTz: endDateTimeTz as string,
      availability: availability as number,
      availabilityUpdated: availabilityUpdated || "",
      booked: booked as number,
      bookable: bookable as number,
      estimatedTotalCapacity: estimatedTotalCapacity as number,
      timeZone: timeZone as string,
      dynamicPrices: computedPrices
        ? computedPrices.map((price) => {
            if (!price) {
              throw new Error("Invalid price in timeslot");
            }
            const {
              strategyPrice,
              isDisplayed,
              isAvailable,
              startValidity,
              endValidity,
              priceValue,
            } = price;

            return {
              priceId: strategyPrice?.priceId as number,
              isDisplayed: isDisplayed as 1 | 0,
              isAvailable: isAvailable as 1 | 0,
              startValidity: startValidity as string,
              endValidity: endValidity as string,
              priceValue: priceValue as number,
            };
          })
        : [],
      ticketGroups:
        psc?.ticketGroups?.map((t) => Number(t?.ticketGroupId)) || [],
      eventKey: eventKey ? eventKey : null,
    };

    if (isParentPC) {
      // So these timeslots pulled from remote are actually linked to a ParentPC
      // lets keep this info in them before inserting them in local db
      timeslot.isParentPC = true;
    }

    timeslots.push(timeslot);
  }

  return timeslots;
}

/**
 *
 * @param timeslots : an array of ids of timeslots, the fields to update and the new values of those fields
 */
export async function updateTimeslots(timeslots: ISyncUpdates[]) {
  // Consider only the timeslots we have in local db
  const ids = timeslots.map((f) => Number(f.id));
  LogService.info(
    `Processing timeslots stock update with attached parameter.`,
    {
      params: {
        newStock: timeslots,
      },
    }
  );

  const updates: Array<{
    id: number;
    availability: number;
    booked: number;
    bookable?: number;
    availabilityUpdated?: string;
  }> = [];

  for (const id of ids) {
    const timeslotUpdates = timeslots.find((ts) => ts.id === String(id));

    if (!timeslotUpdates || !timeslotUpdates.fields) {
      continue;
    }

    const updatesObj: IKeyValue = {};
    for (const field of timeslotUpdates.fields) {
      updatesObj[field.key] = field.value;
    }

    updates.push({
      id,
      // If bookable = 0 then this timeslot is no longer available
      // so set availability to 0 to prevent overbooking
      availability: Number(updatesObj.bookable)
        ? Number(updatesObj.availability)
        : 0,
      booked: Number(updatesObj.booked),
      bookable: Number(updatesObj.bookable),
      availabilityUpdated: String(updatesObj.availabilityUpdated || ""),
    });
  }

  if (updates.length > 0) {
    try {
      await updateTimeslotsAvailability(updates);
    } catch (err: any) {
      LogService.info(
        `Error when updating timeslots local stock. Check attached parameter and payload.`,
        {
          params: { updates },
          error: err,
        },
        LogLevel.Error
      );
    }
  }

  LogService.info(
    `Done updating local stock of timeslots with attached parameter.`,
    {
      params: {
        newStock: timeslots,
      },
    }
  );
}

/**
 * Get all Timeslots of a given date from Dexie
 * parameter date is in the user timezone date
 * dates of the timeslots in db are in UTC
 */
export async function getTimeslotsByDate(
  date: Date,
  ticketGroupId: number
): Promise<ITimeSlot[] | undefined> {
  const midnight1 = dayjs(date).format("YYYY-MM-DD [00:00:00]");
  const midnight2 = dayjs(date).add(1, "d").format("YYYY-MM-DD [00:00:00]");

  try {
    // Query timeslots using Dexie with all date filters
    const results = await queryTimeslotsByTicketGroup({
      ticketGroupId,
      startDateTimeAfter: new Date(midnight1).toISOString(),
      startDateTimeBefore: new Date(midnight2).toISOString(),
      endDateTimeAfter: nowDB(),
    });

    return results.length > 0 ? results : undefined;
  } catch (error) {
    LogService.info("Error in getTimeslotsByDate", error, LogLevel.Error);
    throw error;
  }
}

/**
 * Fetches all the timeslots of a given list of product categories for a specific day
 * and stores them in Dexie database.
 * @param ids Product Category ids
 * @param date
 */
export async function fetchAllPCsTimeslotsByDate(ids: number[], date: string) {
  const limit = 500;
  const skip = 0;
  const startDate = dayjs(date).startOf("day").toDate().setHours(0, 0, 0, 0);
  const endDate = dayjs(date)
    .add(1, "day")
    .startOf("day")
    .toDate()
    .setHours(0, 0, 0, 0);

  const slots = (
    await getPCsTimeSlotsByPeriod(ids, startDate, endDate, {
      skip,
      limit,
    })
  ).timeslots;
  if (!slots || !slots.length) {
    return;
  }
  const localTimeslots = toLocalTimeslots(slots);

  // Use Dexie bulkPut which handles insert/update automatically
  await bulkUpsertTimeslots(localTimeslots);
}

/**
 * Fetches all the timeslots of a given list of product categories for a given time range
 * and stores them in Dexie database.
 * @param ids Product Category ids
 * @param from start date
 * @param to end date
 * @param byStartDateTime if set to true, it compares `from` and `to` with startDateTime, otherwise with endDateTime
 */
export async function fetchAllPCsTimeslotsByTimeRange(
  ids: number[],
  from: Date,
  to: Date,
  byStartDateTime: boolean
) {
  const limit = 500;
  const skip = 0;
  let getTimeSlotsQuery = getPCsTimeSlotsByPeriod;
  if (byStartDateTime) {
    getTimeSlotsQuery = getPCsTimeSlotsByPeriodRelativeToStart;
  }
  const slots = (
    await getTimeSlotsQuery(ids, from.getTime(), to.getTime(), {
      skip,
      limit,
    })
  ).timeslots;
  if (!slots || !slots.length) {
    return;
  }
  const localTimeslots = toLocalTimeslots(slots);

  // Use Dexie bulkPut which handles insert/update automatically
  await bulkUpsertTimeslots(localTimeslots);
}

/**
 * Get timeslots linked to a given categoryId within a day using Dexie
 * @param productCategoryId Product Category Id
 * @param from Start datetime ISO string
 * @param to End datetime ISO string
 * @param limit Max number of results
 */
export async function getFutureTimeSlotsByProductCategory2(
  productCategoryId: number,
  from: string,
  to: string,
  limit?: number
): Promise<ITimeSlot[] | undefined> {
  let results = await queryTimeslotsByProductCategories({
    categoryIds: [Number(productCategoryId)],
    startDateTimeAfter: from,
    startDateTimeBefore: to,
    limit,
  });

  // Filter for minAvailability >= 1 (now done in-memory since removed from Dexie query)
  results = results.filter((ts) => ts.availability >= 1);

  return results.length > 0 ? results : undefined;
}

/**
 * 1- Fetches all timeslots from remote that are linked to local non-recurring seating plan parent product categories
 * 2- Converts them into local format
 * 3- Insert them in Dexie
 */
export async function fetchNonReccuringSeatingPlanTimeslots() {
  try {
    // Get non-recurring Seated parent PCs from Dexie using indexed helper
    const dbPCs = await getNonRecurringSeatingPCs();

    if (!dbPCs.length) {
      return;
    }

    const pcIds = dbPCs.map((pc) => pc.id);

    // Fetch timeslots by categoryId from remote db
    const parentPCsTimeslots = await getPCsTimeSlots(pcIds);

    if (!parentPCsTimeslots.timeslots?.length) {
      return;
    }

    // Convert timeslots into local format and insert in Dexie
    const localFormatTimeslots = toLocalTimeslots(
      parentPCsTimeslots.timeslots,
      true /* isParentPC */
    );
    await bulkUpsertTimeslots(localFormatTimeslots);
  } catch (error) {
    LogService.info(
      "Error in fetchNonReccuringSeatingPlanTimeslots",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * A pending order line's dated-stock identity, used to pull fresh timeslot
 * availability for its category and shared-stock siblings.
 */
export interface TimeslotSyncEntry {
  /** Numeric catalog id of the line's category. */
  categoryId: number;
  /** Stock entities the line's category belongs to. */
  stockIds: number[];
  startDateTime: string;
  endDateTime: string;
}

/**
 * Pulls fresh timeslot availability from the backend for the given entries'
 * categories (expanded to shared-stock siblings) and time ranges, and writes
 * the updates into the local Dexie timeslots.
 */
export async function syncUpdatedTimeslots(
  entries: TimeslotSyncEntry[]
): Promise<void> {
  if (!entries.length) {
    return;
  }

  // build groups of category ids and timeslot intervals
  const groups = new Map<
    string,
    {
      categoryIds: number[];
      timeslots: Set<string>; // for unique intervals
    }
  >();

  for (const entry of entries) {
    let categoryIds: number[] = [];
    if (!entry.stockIds.length) {
      categoryIds.push(entry.categoryId);
    } else {
      // get all categories linked to these stockIds
      try {
        const dbCategories = await getProductCategoriesByStockIds(
          entry.stockIds
        );
        categoryIds.push(...dbCategories.map((pc) => pc.id));
      } catch (error) {
        LogService.info(
          `Error when fetching product categories by stockIds: ${entry.stockIds} from local db.`,
          {
            params: {
              stockIds: entry.stockIds,
              error: error,
            },
          },
          LogLevel.Error
        );
      }
    }

    // sort category ids
    categoryIds = Array.from(new Set(categoryIds)).sort((a, b) => a - b);

    const key = JSON.stringify(categoryIds);

    if (!groups.has(key)) {
      groups.set(key, { categoryIds, timeslots: new Set() });
    }

    const group = groups.get(key)!;
    const start = dayjs(entry.startDateTime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const end = dayjs(entry.endDateTime).utc().format("YYYY-MM-DD HH:mm:ss");
    const timeslotKey = `${start}|${end}`;

    // add the timeslot interval to the group's set and avoid duplicates
    group.timeslots.add(timeslotKey);
  }

  // return if no valid groups
  if (!groups.size) {
    return;
  }

  // build conditions for querying timeslots: one condition per group
  const conditions: string[] = [];
  for (const group of groups.values()) {
    // convert Set to array and sort it
    const sortedTimeslots = Array.from(group.timeslots)
      .map((timeslot) => {
        const [start, end] = timeslot.split("|");
        return { start, end, original: timeslot };
      })
      .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

    const rangeConds: string[] = sortedTimeslots.map(
      ({ start, end }) =>
        `(startDateTime,le,${end})~and(endDateTime,ge,${start})`
    );

    const condition = `(category_id,in,${group.categoryIds.join(
      ","
    )})~and(${rangeConds.join("~or")})`;
    conditions.push(condition);
  }

  // pull updated timeslots
  const updatedTimeslots = await getTimeslotsByCondition(
    conditions.join("~or")
  );
  if (!updatedTimeslots?.length) {
    return;
  }

  // build updates array
  const excludedProps = new Set(["id", "categoryId", "__typename"]);
  const updates: ISyncUpdates[] = updatedTimeslots.map((ts) => {
    const tsFields: ISyncField[] = Object.entries(ts)
      .filter(([prop]) => !excludedProps.has(prop))
      .map(([key, value]) => ({ key, value: String(value) }));

    return { id: String(ts.id), fields: tsFields };
  });

  await updateTimeslots(updates);
}
